/* eslint-disable radix */
/* eslint-disable class-methods-use-this */
import {
  startDrag,
  stopDrag,
  hoverAction,
  changeOverlay,
} from "store/actions/editorActions";
import { addElement, moveElement } from "store/actions/pageActions";
import {
  throttle,
  FileTypes,
  getElementVersions,
  getComponentData,
  createKey,
} from "shared/utils";
import DragOverlay from "shared/DragOverlay";

/**
 * Class responsible for managing our redux state when dragging and sorting
 * elements in the custom page. The motivation behind this is because since
 * we can't register functions in our state with redux, we'll wrap the start
 * and end functions for actions such as drag and sort into a class that
 * then dispatches the necessary actions in order to update our state.
 *
 * @class DragManager
 */
class DragManager {
  /**
   * Creates an instance of DragManager.
   * @param {Object} store Redux Store.
   */
  constructor(store) {
    this.store = store;
    this.overlay = new DragOverlay();
    this.currentDragging = null;
    this.listeners = {};
    this.clear();
  }

  findByFather(state, parentID) {
    return state.page.components.filter((el) => el.parent_id === parentID);
  }

  findByElement(state, element) {
    return state.page.components.filter((el) => el.component === element);
  }

  orderbyPosition(elements, useProps = false) {
    return elements.sort((a, b) => {
      if (useProps) {
        return parseInt(a.props.position) - parseInt(b.props.position);
      }
      return parseInt(a.position) - parseInt(b.position);
    });
  }

  getLast(arr) {
    return arr[arr.length - 1];
  }

  getFirst(arr) {
    return arr[0];
  }

  addToLastSection() {
    if (this.mode === "MOVE_ELEMENT") {
      return;
    }

    const state = this.store.getState();
    const section = this.getLast(
      this.orderbyPosition(this.findByElement(state, "Section"))
    );
    const column = this.getFirst(
      this.orderbyPosition(this.findByFather(state, section.key), true)
    );
    const lastSibling = this.getLast(
      this.orderbyPosition(this.findByFather(state, column.key), true)
    );
    const position = lastSibling ? parseInt(lastSibling.position) + 1 : 1;
    const { component, version, props, editMode } = this.element;
    this.store.dispatch(
      addElement({
        component,
        version: version || "default",
        parent_id: column.key,
        props,
        editMode,
        position,
      })
    );
    this.dispatch(stopDrag());
    this.clear(true);
  }

  /**
   * Clears all information within this instance.
   */
  clear(force = false) {
    this.mode = null;
    this.element = null;
    this.target = null;
    this.targetDOM = null;
    this.childrenMapping = [];
    this.dragType = "Element";
    this.showingOverlay = false;
    if (force) {
      this.currentDragging = null;
    }
    this.overlay.destroy();
    this.dispatch(stopDrag());
  }

  clearOverlay() {
    this.showingOverlay = false;
    this.dispatch(changeOverlay(false));
    this.overlay.destroy();
  }

  showOverlay() {
    if (!this.showingOverlay) {
      this.showingOverlay = true;
      this.dispatch(changeOverlay(true));
      this.overlay.start();
    }
  }

  createElementForFile = (file, position, index) => {
    const { type } = file;
    const component = FileTypes.findByMime(type);
    const version = getElementVersions(component).default;
    const props = getComponentData(component, `V${version}`);
    return {
      component,
      version,
      position,
      key: createKey(),
      parent_id: this.target,
      editMode: true,
      props: {
        ...props,
        preview: true,
        index,
        filePreview: file,
      },
    };
  };

  computeProps = (props) => {
    if (!props) return {};
    return Object.keys(props).reduce((curr, prop) => {
      const beingComputed = props[prop];
      if (
        typeof beingComputed === "object" &&
        beingComputed !== null &&
        beingComputed.needComputing
      ) {
        const res = beingComputed.fn(this.store.getState());
        return {
          ...curr,
          [prop]: res,
        };
      }
      return {
        ...curr,
        [prop]: beingComputed,
      };
    }, {});
  };

  /**
   * Handle dropping onto the iframe page. If dropping
   * files, it'll start the file upload and element
   * generation. Else it'll check whether to add or move
   * elements.
   */
  drop(position, files) {
    this.dragType === "Files"
      ? this.dropFiles(position, files)
      : this.dropElement(position);
    this.dispatch(stopDrag());
    this.clear(true);
  }

  /**
   * For each file received in files, it'll check to see if
   * there's an equivalent element that could be added to the
   * page with the uploaded content.
   *
   * If it does, add the element and start uploading the file. Else,
   * idk.
   *
   * @param {*} position
   * @param {*} files
   * @memberof DragManager
   */
  dropFiles(position, files) {
    [...files].forEach((file, i) => {
      const elementPos = position + i;
      const element = this.createElementForFile(file, elementPos, i);
      this.store.dispatch(addElement(element));
    });
  }

  /**
   * Moves or add an element into the given position within
   * the current target.
   *
   * @param {Number} position position where the element will be placed
   * @memberof DragManager
   */
  dropElement(position) {
    const { key, component, version, props, editMode, name } = this.element;
    if (this.mode === "MOVE_ELEMENT") {
      this.store.dispatch(
        moveElement({
          component: component || name,
          source: key,
          destination: this.target,
          position,
        })
      );
    } else {
      const computedProps = this.computeProps(props);
      this.store.dispatch(
        addElement({
          component,
          version: version || "default",
          parent_id: this.target,
          props: computedProps,
          editMode,
          position,
        })
      );
    }
  }

  /**
   * This invokes a dispatch from our redux store.
   *
   * @param {fn} action Redux Action
   */
  dispatch(action) {
    this.store.dispatch(action);
  }

  /**
   * When a drag starts, register both the method and information about what's
   * being dragged.
   *
   * @param {String} mode Mode identifier, either ADD_ELEMENT or MOVE_ELEMENT
   * @param {Object} element Element being dragged
   */
  registerStart(mode, element) {
    this.mode = mode;
    this.element = element;
    this.showingOverlay = true;
    this.currentDragging = { kind: "Element", action: mode, ...element };
    this.overlay.start();
    this.dispatch(startDrag(element.key, this.elementHeight));
  }

  /**
   *
   *
   * @param {Number} filesAmount
   */
  registerStartFiles(filesAmount) {
    this.dragType = "Files";
    this.showingOverlay = true;
    this.currentDragging = { kind: "File" };
    this.overlay.start();
    this.dispatch(startDrag(null, null, filesAmount));
  }

  /**
   * When an element being dragged enters a valid dropzone, we register
   * the key of the dropzone as our current target, or the father from it
   * (this occurs when something being dragged gets past the element trigger of
   * dragEnter, so we try again on the first possible dragOver).
   * So if a drop action occurs, it means we're dropping on the target s
   * elected.
   *
   * @param {String} targetKey Key of the dropzone
   * @param {Object} document Document reference for finding elements.
   * @param {Boolean} useFather if we should be using father from the element or not
   */
  registerTarget(targetKey, document, useFather = false) {
    this.target = useFather ? this.findFather(targetKey) : targetKey;
    this.targetDOM = document.querySelector(`.${this.target}`);
    this.targetDOM.querySelectorAll(".cp_element").forEach((element) => {
      const { top, height } = element.getBoundingClientRect();
      this.childrenMapping.push({
        top,
        height,
        element,
      });
    });
  }

  hoverOperation = throttle((yPos) => {
    if (this.childrenMapping.length === 0) {
      return;
    }
    // find the closest element the mouse position
    const closest = this.childrenMapping.reduce((prev, curr) => {
      return Math.abs(curr.top - yPos) < Math.abs(prev.top - yPos)
        ? curr
        : prev;
    });
    const { element, top, height } = closest;

    // determine if we're moving up or down in the page.
    const direction = yPos - top < height / 2 ? "above" : "bellow";
    this.dispatch(
      hoverAction({
        hoveringElement: element.elementRef.instance.componentKey(),
        hoverDirection: direction,
      })
    );
  }, 100);

  clearHover() {
    this.dispatch(hoverAction({ hoveringElement: null, hoverDirection: null }));
  }

  /**
   * Given a child key, we traverse our state and find the father.
   *
   * @param {String} childKey key from the children element
   * @return {String} father key
   */
  findFather(childKey) {
    return this.state().page.components.find((el) => el.key === childKey)
      .parent_id;
  }

  /**
   * When the element being dragged leaves a valid dropZone
   * we clear the target so the element doesn't get dropped
   * into something other than the intended target.
   */
  clearTarget() {
    this.target = null;
    this.childrenMapping = [];
    this.store.dispatch(
      hoverAction({
        hoveringElement: null,
        hoverDirection: null,
      })
    );
  }

  /**
   * Check if the current element can be dropped into the
   * given container accepted types.
   *
   * @param {Array} acceptedTypes Array of types that target can have dropped onto
   * @returns {Boolean}
   */
  isValidTarget(acceptedTypes, dragTypes) {
    if (this.dragType === "Element") {
      return this.element
        ? acceptedTypes.includes(this.element.dragType)
        : false;
    }
    return dragTypes.some((dragType) => acceptedTypes.includes(dragType));
  }

  /**
   * Subscribe a listener and a handler function that will be invoked when the
   * event that is being listened to gets triggered.
   *
   * @param {String} listener event name
   * @param {Function} handler handler function
   */
  subscribe(listener, handler) {
    if (this.listeners[listener]) {
      this.listeners[listener].push(handler);
    } else {
      this.listeners[listener] = [handler];
    }
  }

  /**
   * Remove a handler function for the given listener, so the function
   * won't be invoked when the event listener gets triggered.
   *
   * @param {String} listener event name
   * @param {Function} handler handler function
   */
  unsubscribe(listener, handler) {
    const listenerHandlers = this.listeners[listener];
    if (listenerHandlers) {
      const newHandlers = listenerHandlers.filter((fn) => fn !== handler);
      this.listeners[listener] = newHandlers;
    }
  }

  /**
   * Triggers all handlers for a given eventName, passing the
   * eventData as an argument to those handlers
   *
   * @param {String} eventName event name
   * @param {Mixed} eventData eventData
   */
  trigger(eventName, eventData) {
    const listenerHandlers = this.listeners[eventName];
    if (listenerHandlers && listenerHandlers.length) {
      listenerHandlers.forEach((handler) => handler(eventData));
    }
  }

  /**
   * This access the state within our redux store
   * and returns it to the instance.
   *
   * @returns {Object} Redux state
   */
  state() {
    return this.store.getState();
  }
}

export default DragManager;
